import { lazy, Suspense } from "react";
import { defineQuiz, type Answers, type Question } from "@/schema/quizSchema";
import { exposureScore, exposureBand } from "./manly-hormone/scoring";
import { CARDS } from "./manly-hormone/cards";
import { BRAND_LABEL } from "./manly-hormone/copy";
import { assetUrl } from "./manly-hormone/assets";
import { BuildingRows } from "./manly-hormone/BuildingRows";
// Eager: the building screen shares these classes and renders BEFORE the lazy
// funnel bundle loads. Same reasoning as manly-quiz.tsx.
import baseCss from "./manly/report.tokens.css?inline";
import hormoneCss from "./manly-hormone/report.tokens.css?inline";

const HormoneFunnel = lazy(() => import("./manly-hormone/report"));

/** Build an info Question from the CARDS table, so length stays under test.
 *  Return type is narrowed to the "info" branch of the Question union (rather
 *  than the full union the brief's prose describes) because this always
 *  constructs an info-type object at runtime: spreading a still-unioned return
 *  type into `{ ...card("proof"), testimonial: {...} }` below fails TS excess-
 *  property-checking against the 5 non-info union members that lack
 *  `testimonial`. No runtime/content change, just a tighter, accurate type. */
function card(id: string, subs?: Record<string, string>): Extract<Question, { type: "info" }> {
  const c = CARDS[id];
  if (!c) throw new Error(`unknown card: ${id}`);
  const sub = (s: string) =>
    Object.entries(subs ?? {}).reduce((acc, [k, v]) => acc.split(`{${k}}`).join(v), s);
  return { id, type: "info", heading: sub(c.heading), body: c.body.map(sub) };
}

/** One bridge per adult brand, so the villain is named in the copy. Carries the
 *  client's villain graphic (an Old Spice bottle with its ingredient line
 *  zoomed in; the baked-in text names all three adult brands), which is why
 *  the natural/store bridges, whose villains it does not name, go without. */
function adultBridge(brand: string): Question {
  return {
    ...card("bridge-adult", { BRAND: BRAND_LABEL[brand] }),
    id: `bridge-adult-${brand}`,
    image: assetUrl("hormone-bridge-villain.webp"),
    imageAlt:
      "An Old Spice body wash with its ingredient label magnified. All Old Spice, Axe and Dove: synthetic fragrance that masks smell, odor returns within the hour, bacteria left alive on skin.",
  };
}

const QUESTIONS: Question[] = [
  // ACT 1 - the boy
  {
    id: "age",
    type: "single",
    prompt: "How old is your son?",
    options: [
      { value: "9-10", label: "9–10" },
      { value: "11-12", label: "11–12" },
      { value: "13-14", label: "13–14" },
      { value: "15-16", label: "15–16" },
      { value: "17plus", label: "17+" },
    ],
  },
  {
    id: "energy",
    type: "single",
    prompt: "When he gets home from school, what does he usually do?",
    whyWeAsk: "Afternoon energy is what parents notice first, so it is where we start.",
    options: [
      { value: "done", label: "Straight to his room, and that is him done for the day" },
      { value: "flat", label: "Flat. Not much interest in anything" },
      { value: "resurfaces", label: "Screen or bed for an hour, then he resurfaces" },
      { value: "training", label: "Snack, then he is out or training" },
      { value: "fine", label: "He has energy right through to bedtime" },
    ],
  },
  {
    id: "build",
    type: "single",
    prompt: "Compared to boys his age, how is he filling out?",
    whyWeAsk: "This is not a growth chart. We are asking whether you have been noticing a gap.",
    options: [
      { value: "behind", label: "He is noticeably behind his friends" },
      { value: "abit", label: "A bit behind, and I have thought about it" },
      { value: "hardtosay", label: "Hard to say, they are all different" },
      { value: "ontrack", label: "Right on track" },
      { value: "ahead", label: "Ahead, if anything" },
    ],
  },
  {
    id: "chest",
    type: "single",
    prompt: "Have you noticed any softness or puffiness around his chest?",
    helper: "Nobody asks this and most parents have wondered.",
    options: [
      { value: "bothers", label: "Yes, and it bothers him" },
      { value: "babyfat", label: "Yes, but I assumed it was baby fat" },
      { value: "maybe", label: "Maybe, I have wondered" },
      { value: "no", label: "No" },
      { value: "notlooked", label: "I have not looked closely" },
    ],
  },
  {
    id: "drive",
    type: "single",
    prompt: "Has he gone quiet on you?",
    options: [
      { value: "quiet", label: "He has stopped pushing back, and I miss it" },
      { value: "shortfuse", label: "Short fuse, snaps at everything" },
      { value: "both", label: "Both, depending on the day" },
      { value: "some", label: "Some of that, but he is still himself" },
      { value: "nochange", label: "No change" },
    ],
  },
  {
    id: "odor",
    type: "single",
    prompt: "After he showers, how long before you can smell him again?",
    whyWeAsk: "It is the fastest thing to change, so it becomes your proof anything is working.",
    options: [
      { value: "hour", label: "Within the hour" },
      { value: "schoolday", label: "By the end of the school day" },
      { value: "physical", label: "The second he does anything physical" },
      { value: "never", label: "Honestly, it never fully goes" },
      { value: "notanissue", label: "That is not really an issue for him" },
    ],
  },
  {
    id: "doctor",
    type: "single",
    prompt: "Have you raised any of this with his doctor?",
    options: [
      { value: "boysvary", label: "Yes. Nothing alarming, boys vary" },
      { value: "bloodwork", label: "Yes. Bloodwork came back normal" },
      { value: "notyet", label: "Not yet" },
      { value: "thought", label: "I have thought about it and have not" },
      { value: "never", label: "No, it had not crossed my mind" },
    ],
  },
  card("doctor-seen"),
  card("doctor-not"),
  {
    id: "onset",
    type: "single",
    prompt: "How long have you been telling yourself he would grow out of it?",
    options: [
      { value: "months", label: "A few months" },
      { value: "year", label: "About a year" },
      { value: "twoplus", label: "Two years or more" },
      { value: "neverput", label: "I have never let myself put it that way" },
    ],
  },

  // ACT 2 - the villain and the release
  {
    id: "brand",
    type: "single",
    prompt: "What is he washing with?",
    options: [
      { value: "oldspice", label: "Old Spice" },
      { value: "axe", label: "Axe" },
      { value: "dovemen", label: "Dove Men" },
      { value: "store", label: "Suave, Equate or a store brand" },
      { value: "natural", label: "A “natural” or “clean” brand" },
      { value: "mix", label: "Mix of whatever is on sale" },
      { value: "noidea", label: "No idea, he buys it" },
    ],
  },
  adultBridge("oldspice"),
  adultBridge("axe"),
  adultBridge("dovemen"),
  card("bridge-natural"),
  card("bridge-store"),
  card("symptom-chest"),
  card("symptom-general"),
  {
    id: "want",
    type: "single",
    prompt: "If one thing came back for him, what would you pick?",
    options: [
      { value: "energy", label: "His energy" },
      { value: "drive", label: "His drive, wanting to do things again" },
      { value: "body", label: "His body catching up" },
      { value: "confidence", label: "His confidence" },
      { value: "clean", label: "Just knowing nothing on him is working against him" },
    ],
  },
  {
    ...card("proof"),
    // Client assets (2026-08-20): a customer's son holding his Manly above the
    // body, and a different customer's photo as the circular avatar beside
    // the quote (the landing quote keeps Tracey's).
    image: assetUrl("hormone-proof-boy.webp"),
    imageAlt: "A teenage boy smiling and holding up his Manly.",
    testimonial: {
      quote:
        "My 14 year old son had terrible BO. Straight away, after the first wash he smelled amazing!",
      author: "Tracey M., Verified Customer",
      avatar: assetUrl("hormone-proof-avatar.webp"),
      stars: 5,
    },
  },

  // ACT 3 - quantify it
  {
    id: "products",
    type: "multi",
    prompt: "What else is on him on a normal morning?",
    helper: "Select all that apply.",
    min: 0,
    options: [
      { value: "shampoo", label: "Shampoo or 2-in-1" },
      { value: "deodorant", label: "Deodorant or antiperspirant" },
      { value: "spray", label: "Body spray" },
      { value: "hair", label: "Hair product" },
      { value: "facewash", label: "Face wash" },
      { value: "cologne", label: "Cologne" },
      { value: "justwash", label: "Just the wash", exclusive: true },
    ],
  },
  {
    id: "duration",
    type: "single",
    prompt: "How long has that been the routine, more or less?",
    options: [
      { value: "under1", label: "Under a year" },
      { value: "1to2", label: "One to two years" },
      { value: "3to4", label: "Three to four years" },
      { value: "since", label: "Since he was little" },
    ],
  },
  {
    id: "heat",
    type: "single",
    prompt: "How hot does he shower?",
    whyWeAsk: "Heat opens the skin barrier, so the same product delivers more through it.",
    options: [
      { value: "steam", label: "Steam on the mirror hot" },
      { value: "warm", label: "Warm" },
      { value: "cool", label: "Quick and not that hot" },
      { value: "noidea", label: "No idea" },
    ],
  },

  // ACT 4 - the bottle
  {
    id: "bottle",
    type: "single",
    prompt: "Go get his body wash. Turn it over.",
    helper:
      "Look for one word: fragrance. We will wait. Most useful thirty seconds you will spend today.",
    options: [
      { value: "found", label: "Found it" },
      { value: "clean", label: "It is not on there" },
      { value: "later", label: "I will check later" },
    ],
  },
  card("bottle-found"),
  card("bottle-clean"),
  card("bottle-later"),

  // ACT 5 - objections and lock
  {
    id: "objection",
    type: "single",
    prompt: "What is the part you are least sure about?",
    options: [
      { value: "onepro", label: "Whether swapping one product changes anything" },
      { value: "useit", label: "Whether he will even use it" },
      { value: "clean", label: "Whether “clean” brands are just marketing" },
      { value: "price", label: "The price" },
      { value: "ready", label: "Nothing. I want him off this stuff" },
    ],
  },
  card("obj-onepro"),
  card("obj-useit"),
  card("obj-clean"),
  card("obj-price"),
  card("obj-ready"),
  {
    id: "lock",
    type: "single",
    prompt:
      "Last one. If the products turn out to be part of this, are you swapping his shower this week?",
    options: [
      { value: "today", label: "Yes, today" },
      { value: "afterlabels", label: "Yes, once I have read the labels myself" },
      { value: "seefirst", label: "I want to see what you found first" },
    ],
  },
];

const str = (a: Answers, k: string): string => (typeof a[k] === "string" ? (a[k] as string) : "");

/** Every routed card has a variant for EVERY answer, so no path is shorter than
 *  another and progressTotal stays exact. */
const NEXT: Record<string, string | null> = {
  age: "energy",
  energy: "build",
  build: "chest",
  chest: "drive", // deliberately no card here; see the spec, screen 4
  drive: "odor",
  odor: "doctor",
  "doctor-seen": "onset",
  "doctor-not": "onset",
  onset: "brand",
  "symptom-chest": "want",
  "symptom-general": "want",
  want: "proof",
  proof: "products",
  products: "duration",
  duration: "heat",
  heat: "bottle",
  "bottle-found": "objection",
  "bottle-clean": "objection",
  "bottle-later": "objection",
  "obj-onepro": "lock",
  "obj-useit": "lock",
  "obj-clean": "lock",
  "obj-price": "lock",
  "obj-ready": "lock",
  lock: null,
};

const ADULT_BRANDS = new Set(["oldspice", "axe", "dovemen"]);
const CHEST_COHORT = new Set(["bothers", "babyfat", "maybe"]);
const BRIDGE_IDS = new Set([
  "bridge-adult-oldspice", "bridge-adult-axe", "bridge-adult-dovemen",
  "bridge-natural", "bridge-store",
]);

export const manlyHormoneQuiz = defineQuiz({
  id: "manly-endocrine-exposure-check",
  slug: "manly-hormone-quiz",
  tenant: "manly",
  meta: {
    title: "The Endocrine Exposure Check",
    introHeadline: "Find out if your teenage son's body wash has been holding him back.",
    introBody:
      "Takes two minutes. We'll look at his symptoms, his shower routine, and what's actually in the bottle he uses every day. Then you'll see which products are working against him, and what to swap first.",
    testimonial: {
      quote:
        "I kept blaming him. Lazy, moody, checked out. Then I realized I'd been handing him this stuff every day and never once flipped the bottle over.",
      // Same face and name as the main quiz's landing quote (user request):
      // the funnels share Tracey as THE testimonial voice and the /manly/
      // asset directory, so this is the identical photo the B.O. quiz ships.
      author: "Tracey M., Verified Customer",
      avatar: assetUrl("testimonial-tracey.jpg"),
      stars: 5,
    },
    locale: "en",
    // 16 questions + 6 routed cards (one variant each, always rendered) = 22.
    // Asserted against a real path walk in manlyHormoneQuiz.test.tsx.
    progressTotal: 22,
  },
  questions: QUESTIONS,
  personas: [
    {
      key: "active",
      label: "Active",
      result: {
        tagline: "Going on him daily.",
        body: ["It has not been running long, which is the best position anyone taking this gets."],
      },
    },
    {
      key: "sustained",
      label: "Sustained",
      result: {
        tagline: "Years deep, and the routine is set.",
        body: ["This is where most boys his age land."],
      },
    },
    {
      key: "saturated",
      label: "Saturated",
      result: {
        tagline: "Stacked products, hot water, years of it.",
        body: ["Straight through the window that builds him."],
      },
    },
  ],

  branching: (answers, currentQuestionId) => {
    if (currentQuestionId === "doctor") {
      return ["boysvary", "bloodwork"].includes(str(answers, "doctor"))
        ? "doctor-seen"
        : "doctor-not";
    }
    if (currentQuestionId === "brand") {
      const b = str(answers, "brand");
      if (ADULT_BRANDS.has(b)) return `bridge-adult-${b}`;
      if (b === "natural") return "bridge-natural";
      return "bridge-store";
    }
    if (BRIDGE_IDS.has(currentQuestionId)) {
      return CHEST_COHORT.has(str(answers, "chest")) ? "symptom-chest" : "symptom-general";
    }
    if (currentQuestionId === "bottle") {
      const v = str(answers, "bottle");
      return v === "found" ? "bottle-found" : v === "clean" ? "bottle-clean" : "bottle-later";
    }
    if (currentQuestionId === "objection") {
      const v = str(answers, "objection");
      const known = ["onepro", "useit", "clean", "price", "ready"];
      return `obj-${known.includes(v) ? v : "onepro"}`;
    }
    return NEXT[currentQuestionId] ?? null;
  },

  classifier: (answers) => exposureBand(exposureScore(answers)).toLowerCase(),

  styles: `${baseCss}\n${hormoneCss}`,

  renderBuilding: ({ answers, onDone }) => <BuildingRows answers={answers} onDone={onDone} />,

  renderResult: ({ answers, trackCta }) => (
    <Suspense fallback={<div style={{ minHeight: 240 }} aria-busy="true" />}>
      <HormoneFunnel answers={answers} trackCta={trackCta} />
    </Suspense>
  ),
});
